import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { decodeAuthToken, HStack, VStack } from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import { ScheduleParams } from "../../../types/common";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { AssociatedUsers } from "../../../utils/interfaces/Companies";
import { useAuthStore } from "../../../store";

interface ScheduleSectionProps {
  value: ScheduleParams | undefined;
  onChange: (params: ScheduleParams | undefined) => void;
  users: AssociatedUsers[];
}

function formatNextRun(intervalDays: number): string {
  if (!intervalDays || intervalDays <= 0) return "";
  const next = new Date();
  next.setDate(next.getDate() + intervalDays);
  return next.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  value,
  onChange,
  users: associatedUsers,
}) => {
  const [enabled, setEnabled] = useState(false);

  const recipients = value?.recipients ?? [];
  const intervalDays = value?.intervalDays ?? 1;
  const [rawDays, setRawDays] = useState<string>(String(intervalDays));

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    if (!next) {
      onChange(undefined);
    } else {
      onChange({ recipients, intervalDays });
    }
  };

  const { data: users } = useFilteredAssociatedUsers(associatedUsers);

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setRawDays(raw);
  };

  const handleIntervalBlur = () => {
    const parsed = parseInt(rawDays, 10);
    const days = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    setRawDays(String(days));
    onChange({ recipients, intervalDays: days });
  };

  const userOptions = users.map((u) => ({ value: u.name }));
  const selectedNames = users
    .filter((u) => u.userId && recipients.includes(String(u.userId)))
    .map((u) => u.name);

  const handleUserSelection = (names: string[]) => {
    const ids = users
      .filter((u) => names.includes(u.name) && u.userId)
      .map((u) => String(u.userId));
    onChange({ recipients: ids, intervalDays });
  };

  const nextRunLabel =
    enabled && intervalDays > 0 ? formatNextRun(intervalDays) : "";

  return (
    <VStack className="w-full gap-3">
      {/* Toggle row */}
      <HStack className="items-center gap-2">
        <input
          type="checkbox"
          id="schedule-toggle"
          checked={enabled}
          onChange={handleToggle}
          className="accent-orange-501 w-4 h-4 cursor-pointer"
        />
        <label
          htmlFor="schedule-toggle"
          className="text-sm font-medium text-gray-700 cursor-pointer select-none"
        >
          Schedule this report
        </label>
      </HStack>

      {/* Schedule fields */}
      {enabled && (
        <VStack className="gap-4 pl-6 border-l-2 border-orange-200">
          {/* Recipients */}
          <VStack className="gap-1">
            <Label className="text-sm font-normal">Recipients</Label>
            <MultiSelectV2
              _className="w-full"
              multiple
              placeholder="-- Select users --"
              options={userOptions}
              optionValues={selectedNames}
              handleSelection={handleUserSelection}
            />
          </VStack>

          {/* Interval */}
          <HStack className="gap-6 items-start">
            <VStack className="gap-1 w-40">
              <Label className="text-sm font-normal">Repeat every (days)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={rawDays}
                onChange={handleIntervalChange}
                onBlur={handleIntervalBlur}
                className="w-full"
              />
            </VStack>

            {nextRunLabel && (
              <VStack className="gap-1 justify-end pt-6">
                <HStack className="items-center gap-1 text-xs text-gray-500">
                  <Icon
                    icon="mdi:clock-outline"
                    width={14}
                    className="text-orange-400"
                  />
                  <span>
                    Next execution:{" "}
                    <span className="font-medium text-gray-700">
                      {nextRunLabel}
                    </span>
                  </span>
                </HStack>
              </VStack>
            )}
          </HStack>
        </VStack>
      )}
    </VStack>
  );
};

const INTERNAL_DOMAINS = ["rulezero.com", "hissa.com"];
const EXCLUSION_LIST = INTERNAL_DOMAINS.map((domain) => `@${domain}`);

/**
 * Returns associated users, filtering out `@xyz.com` accounts
 * unless the currently logged-in user's email is itself an `@xyz.com` address.
 */
export function useFilteredAssociatedUsers(users: AssociatedUsers[]) {
  const { accessToken } = useAuthStore();

  const decodedEmail: string = accessToken
    ? decodeAuthToken(accessToken)?.emailId ?? ""
    : "";

  const isInternalUser = INTERNAL_DOMAINS.some((domain) =>
    decodedEmail.toLowerCase().endsWith(`@${domain}`)
  );

  const data = users
    ? isInternalUser
      ? users
      : users.filter(
          (u) =>
            !EXCLUSION_LIST.some((domain) =>
              u.email.toLowerCase().endsWith(domain)
            )
        )
    : users;

  return { data };
}

export default ScheduleSection;
